/**
 * Every write the tenant user list can make, and whether one is in flight.
 *
 * One pending flag for the whole screen: the writes all touch the same list
 * and the caller reloads it after each, so two of them at once would race for
 * the same rows. A failure is reported beside the control that caused it.
 *
 * @module components/admin/useTenantUsers
 */

import { useState } from "react";

import { parseAddressList } from "@/components/shared/address-list";
import type { ProgrammePlatformClient, UserSummary } from "@/platform";
import type { TenantAccountRole } from "@/server/types";

export interface InviteRequest {
  name: string;
  email: string;
  /** Comma- or newline-separated. Other addresses that reach the same person. */
  alternateEmails: string;
  role: string;
  canCreateProgramme: boolean;
}

export interface TenantUsersState {
  pending: boolean;
  /** The invitation form's last failure. */
  error: string | null;
  /** The last failure of a write made from the user table. */
  tableError: string | null;
  /** Resolves true when the invitation was sent, so the form can clear itself. */
  invite: (request: InviteRequest) => Promise<boolean>;
  changeRole: (userId: string, role: string) => Promise<void>;
  toggleProgrammeCreation: (user: UserSummary) => Promise<void>;
  toggleStatus: (user: UserSummary) => Promise<void>;
  resendInvitation: (user: UserSummary) => Promise<void>;
  /** Resolves true when the address was accepted, so the row can stop editing. */
  changeEmail: (userId: string, email: string) => Promise<boolean>;
}

/**
 * @param client - The platform client to write through.
 * @param tenantId - The tenant whose users these are.
 * @param onChanged - Reloads the caller's user list after a successful write.
 * @returns The writes, and what the last one said.
 *
 * @example
 * ```tsx
 * const users = useTenantUsers(client, tenant.id, refreshPlatformWorkspace);
 * ```
 */
export function useTenantUsers(
  client: ProgrammePlatformClient,
  tenantId: string,
  onChanged: () => Promise<void>,
): TenantUsersState {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ from: "invite" | "table"; message: string } | null>(
    null,
  );

  const write = async (
    fallback: string,
    run: () => Promise<unknown>,
    from: "invite" | "table" = "table",
  ): Promise<boolean> => {
    setPending(true);
    setFailure(null);
    try {
      await run();
    } catch (cause) {
      setFailure({ from, message: cause instanceof Error ? cause.message : fallback });
      setPending(false);
      return false;
    }

    try {
      await onChanged();
    } catch (cause) {
      setFailure({
        from,
        message: cause instanceof Error ? cause.message : "Unable to refresh users",
      });
    } finally {
      setPending(false);
    }
    return true;
  };

  const invite = async (request: InviteRequest): Promise<boolean> => {
    const email = request.email.trim();
    if (!email) {
      return false;
    }
    const alternateEmails = parseAddressList(request.alternateEmails);
    return write(
      "Unable to invite the tenant user",
      () =>
        client.inviteTenantUser({
          tenantId,
          email,
          ...(alternateEmails.length ? { alternateEmails } : {}),
          displayName: request.name.trim() || undefined,
          accountRole: request.role as TenantAccountRole,
          tenantCapabilities: request.canCreateProgramme ? ["programme:create"] : [],
        }),
      "invite",
    );
  };

  const changeRole = async (userId: string, role: string): Promise<void> => {
    await write("Unable to change the tenant role", () =>
      client.changeTenantUserRole({ tenantId, userId, accountRole: role as TenantAccountRole }),
    );
  };

  const toggleProgrammeCreation = async (user: UserSummary): Promise<void> => {
    const held = user.tenantCapabilities ?? [];
    const allowed = held.includes("programme:create");
    const tenantCapabilities = allowed
      ? held.filter((capability) => capability !== "programme:create")
      : [...held, "programme:create" as const];
    await write("Unable to change programme creation access", () =>
      client.changeTenantUserCapabilities({ tenantId, userId: user.id, tenantCapabilities }),
    );
  };

  const toggleStatus = async (user: UserSummary): Promise<void> => {
    await write("Unable to change the user status", () =>
      client.changeTenantUserStatus({
        tenantId,
        userId: user.id,
        status: user.status === "active" ? "deactivated" : "active",
      }),
    );
  };

  const resendInvitation = async (user: UserSummary): Promise<void> => {
    await write("Unable to resend the invitation email", () =>
      client.resendTenantInvitation({ tenantId, userId: user.id }),
    );
  };

  const changeEmail = async (userId: string, email: string): Promise<boolean> => {
    const trimmed = email.trim();
    if (!trimmed) {
      return false;
    }
    return write("Unable to change the user's email address", () =>
      client.changeTenantUserEmail({ tenantId, userId, email: trimmed }),
    );
  };

  return {
    pending,
    error: failure?.from === "invite" ? failure.message : null,
    tableError: failure?.from === "table" ? failure.message : null,
    invite,
    changeRole,
    toggleProgrammeCreation,
    toggleStatus,
    resendInvitation,
    changeEmail,
  };
}
